import React from 'react';

const Skeleton = ({ 
  variant = 'text', 
  width, 
  height,
  className = '',
  count = 1,
}) => {
  const baseClasses = 'bg-surface-overlay rounded animate-shimmer bg-[length:200%_100%] bg-gradient-to-r from-surface-overlay via-surface-border/40 to-surface-overlay';

  const variantClasses = {
    text: 'h-4 rounded',
    title: 'h-5 rounded',
    avatar: 'rounded-full',
    card: 'rounded-lg',
    button: 'h-9 rounded-md',
    thumbnail: 'rounded-lg aspect-video',
  };

  const getStyles = () => {
    const styles = {};
    if (width) styles.width = typeof width === 'number' ? `${width}px` : width;
    if (height) styles.height = typeof height === 'number' ? `${height}px` : height;
    
    if (!width) {
      switch (variant) {
        case 'avatar':
          styles.width = '36px';
          styles.height = '36px';
          break;
        case 'text':
          styles.width = '100%';
          break;
        case 'title':
          styles.width = '60%';
          break;
        case 'button':
          styles.width = '100px';
          break;
        default:
          styles.width = '100%';
      }
    }
    
    return styles;
  };

  const combinedClasses = `${baseClasses} ${variantClasses[variant] || variantClasses.text} ${className}`.trim();

  if (count > 1) {
    return (
      <div className="space-y-2.5">
        {Array.from({ length: count }).map((_, i) => (
          <div 
            key={i} 
            className={combinedClasses} 
            style={{ 
              ...getStyles(), 
              width: variant === 'text' && i === count - 1 ? '80%' : getStyles().width 
            }} 
          />
        ))}
      </div>
    );
  }

  return <div className={combinedClasses} style={getStyles()} />;
};

export const SkeletonCard = ({ className = '' }) => (
  <div className={`p-5 bg-surface-raised border border-surface-border rounded-lg ${className}`}>
    <div className="flex items-center gap-3 mb-4">
      <Skeleton variant="avatar" />
      <div className="flex-1 space-y-2">
        <Skeleton variant="title" width="50%" />
        <Skeleton variant="text" width="30%" />
      </div>
    </div>
    <Skeleton variant="text" count={2} />
  </div>
);

export const SkeletonTable = ({ rows = 5, columns = 4, className = '' }) => (
  <div className={`bg-surface-raised border border-surface-border rounded-lg overflow-hidden ${className}`}>
    <div className="p-3 border-b border-surface-border grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} variant="text" width="60%" />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div 
        key={rowIndex} 
        className="p-3 border-b border-surface-border/50 last:border-b-0 grid gap-4" 
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Skeleton key={colIndex} variant="text" width={colIndex === 0 ? '80%' : '50%'} />
        ))}
      </div>
    ))}
  </div>
);

export const SkeletonList = ({ count = 3, className = '' }) => (
  <div className={`space-y-3 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

export default Skeleton;
